/**
 * 游戏框架基类
 * 所有游戏必须继承此类
 */
export class GameFramework {
  constructor(container, options = {}) {
    this.container = container;
    this.options = options;
    this.lang = options.lang || 'zh';
    this.difficulty = options.difficulty || 'easy';
    this.isInitialized = false;
    this.isPlaying = false;
    this.score = 0;
    this.timer = 0;
    this.timerInterval = null;
    
    // 事件回调
    this.onScoreUpdate = options.onScoreUpdate || (() => {});
    this.onTimerUpdate = options.onTimerUpdate || (() => {});
    this.onComplete = options.onComplete || (() => {});
    this.onError = options.onError || (() => {});
    
    // i18n 数据（子类应覆盖）
    this.i18n = {
      zh: {},
      en: {}
    };
  }
  
  /**
   * 初始化游戏 - 必须被子类实现
   */
  async init() {
    throw new Error('init() must be implemented by subclass');
  }
  
  /**
   * 开始/重新开始游戏
   */
  start() {
    this.isPlaying = true;
    this.score = 0;
    this.timer = 0;
    this.onScoreUpdate(this.score);
    this.startTimer();
  }
  
  /**
   * 暂停游戏
   */
  pause() {
    this.isPlaying = false;
    this.stopTimer();
  }
  
  /**
   * 恢复游戏
   */
  resume() {
    this.isPlaying = true;
    this.startTimer();
  }
  
  /**
   * 设置难度
   * @param {string} level - 'easy' | 'medium' | 'hard'
   */
  setDifficulty(level) {
    this.difficulty = level;
    if (this.isInitialized) {
      this.start();
    }
  }
  
  /**
   * 设置语言
   * @param {string} lang - 'zh' | 'en'
   */
  setLanguage(lang) {
    this.lang = lang;
    this.updateUI();
  }
  
  /**
   * 更新界面语言
   */
  updateUI() {
    if (!this.container) return;
    this.container.querySelectorAll('[data-i18n]').forEach(el => {
      const key = el.getAttribute('data-i18n');
      const text = this.t(key);
      if (text !== key) {
        el.textContent = text;
      }
    });
  }
  
  /**
   * 翻译
   * @param {string} key - 翻译键
   * @returns {string} 翻译文本
   */
  t(key) {
    const translations = this.i18n[this.lang] || this.i18n.zh;
    return translations[key] || key;
  }
  
  /**
   * 销毁游戏 - 清理资源
   */
  destroy() {
    this.isPlaying = false;
    this.stopTimer();
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
  
  /**
   * 获取当前状态
   * @returns {object} 可序列化的游戏状态
   */
  getState() {
    return {
      score: this.score,
      timer: this.timer,
      difficulty: this.difficulty,
      isPlaying: this.isPlaying
    };
  }
  
  /**
   * 恢复状态
   * @param {object} state - 之前保存的状态
   */
  setState(state) {
    if (state.score !== undefined) this.score = state.score;
    if (state.timer !== undefined) this.timer = state.timer;
    if (state.difficulty) this.difficulty = state.difficulty;
  }
  
  /**
   * 增加分数
   * @param {number} points - 增加的分数
   */
  addScore(points) {
    this.score += points;
    this.onScoreUpdate(this.score);
  }
  
  /**
   * 完成游戏
   */
  complete() {
    this.isPlaying = false;
    this.stopTimer();
    this.onComplete({
      score: this.score,
      time: this.timer
    });
  }
  
  /**
   * 启动计时器
   */
  startTimer() {
    this.stopTimer();
    this.timerInterval = setInterval(() => {
      this.timer++;
      this.onTimerUpdate(this.timer);
    }, 1000);
  }
  
  /**
   * 停止计时器
   */
  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
  
  /**
   * 格式化时间显示
   * @param {number} seconds - 秒数
   * @returns {string} 格式化后的时间 MM:SS
   */
  formatTime(seconds) {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }
  
  // ========== DOM 工具方法 ==========
  
  /**
   * 创建 DOM 元素
   * @param {string} tag - 标签名
   * @param {string} className - 类名
   * @param {HTMLElement} parent - 父元素
   * @returns {HTMLElement}
   */
  createElement(tag, className, parent) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (parent) parent.appendChild(el);
    return el;
  }
  
  /**
   * 创建带文本的元素
   * @param {string} tag - 标签名
   * @param {string} text - 文本内容
   * @param {string} className - 类名
   * @param {HTMLElement} parent - 父元素
   * @returns {HTMLElement}
   */
  createTextElement(tag, text, className, parent) {
    const el = this.createElement(tag, className, parent);
    el.textContent = text;
    return el;
  }
  
  /**
   * 创建按钮
   * @param {string} text - 按钮文本
   * @param {Function} onClick - 点击回调
   * @param {string} className - 额外类名
   * @param {HTMLElement} parent - 父元素
   * @returns {HTMLButtonElement}
   */
  createButton(text, onClick, className = '', parent) {
    const btn = this.createElement('button', `btn ${className}`, parent);
    btn.textContent = text;
    if (onClick) btn.addEventListener('click', onClick);
    return btn;
  }
  
  /**
   * 绑定事件（自动清理）
   * @param {HTMLElement} element - 目标元素
   * @param {string} event - 事件名
   * @param {Function} handler - 处理函数
   * @param {object} options - 事件选项
   */
  bindEvent(element, event, handler, options = {}) {
    element.addEventListener(event, handler, options);
    // 保存引用以便销毁时清理
    if (!this._eventListeners) this._eventListeners = [];
    this._eventListeners.push({ element, event, handler });
  }
}
